import { PatientDetailModelViewer } from '../PatientDetailModelViewer.graphql';
import { PatientDetailCard } from './PatientDetailCard';
import { PatientScanCard, SCAN_CARD_WIDTH } from './scan-card/PatientScanCard.graphql';
import type { FragmentType, PatientScanCardPatient_FragmentFragmentDoc } from '@orthly/graphql-inline-react';
import {
    graphql,
    getFragmentData,
    PatientDetailMainScanExport_FragmentFragmentDoc,
} from '@orthly/graphql-inline-react';
import { styled, Text } from '@orthly/ui-primitives';
import React from 'react';

const ScansContainer = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: `minmax(${SCAN_CARD_WIDTH}px, 1fr) 3fr`,
    gap: '12px',
    [theme.breakpoints.down('lg')]: {
        gridTemplateColumns: '1fr',
        gridTemplateRows: '150px 400px',
    },
}));

const ScansInnerContainer = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    height: '500px',
    overflow: 'scroll',
    [theme.breakpoints.down('lg')]: {
        flexDirection: 'row',
        height: 'unset',
    },
}));

const PatientDetailScansCardScan_Fragment = graphql(`
    fragment PatientDetailScansCardScan_Fragment on PatientScan {
        ...PatientScanCardScan_Fragment
        scan_export_id
    }
`);

interface PatientDetailScansCardProps {
    patientFragment: FragmentType<typeof PatientScanCardPatient_FragmentFragmentDoc>;
    scanFragments: readonly FragmentType<typeof PatientDetailScansCardScan_Fragment>[];
    selectedScanExportFragment: FragmentType<typeof PatientDetailMainScanExport_FragmentFragmentDoc> | undefined;
    selectedScanExportLoading: boolean;
    selectedScanIndex: number | null;
    setSelectedScanIndex: (index: number | null) => void;
    setSelectedScanExportId: (id: string | null) => void;
    setShareWithPatientModalOpen: (open: boolean) => void;
}

export const PatientDetailScansCard: React.VFC<PatientDetailScansCardProps> = props => {
    const {
        patientFragment,
        scanFragments,
        selectedScanExportFragment,
        selectedScanExportLoading,
        selectedScanIndex,
        setSelectedScanIndex,
        setSelectedScanExportId,
        setShareWithPatientModalOpen,
    } = props;

    const scanCardFragments = getFragmentData(PatientDetailScansCardScan_Fragment, scanFragments);

    const selectedScanCardFragment = React.useMemo(() => {
        if (selectedScanIndex === null) {
            return null;
        }

        return scanCardFragments[selectedScanIndex];
    }, [selectedScanIndex, scanCardFragments]);

    const selectedScanExport = getFragmentData(
        PatientDetailMainScanExport_FragmentFragmentDoc,
        selectedScanExportFragment,
    );

    return (
        <PatientDetailCard gridArea={'scans'}>
            {scanCardFragments.length === 0 && (
                <Text medium variant={'body1'} color={'LIGHT_GRAY'} align={'center'}>
                    No Scans
                </Text>
            )}

            {scanCardFragments.length > 0 && (
                <ScansContainer>
                    <ScansInnerContainer>
                        {scanCardFragments.map((scanCardFragment, idx) => (
                            <PatientScanCard
                                key={idx}
                                idx={idx}
                                patientScanCardFragment={patientFragment}
                                scanCardFragment={scanCardFragment}
                                selectedScanExportFragment={selectedScanExport}
                                selectedScanCardFragment={selectedScanCardFragment}
                                selectedScanExportLoading={selectedScanExportLoading}
                                selectedScanIndex={selectedScanIndex}
                                setSelectedScanIndex={setSelectedScanIndex}
                                setSelectedScanExportId={setSelectedScanExportId}
                                setShareWithPatientModalOpen={setShareWithPatientModalOpen}
                            />
                        ))}
                    </ScansInnerContainer>

                    <PatientDetailModelViewer
                        selectedScanExportFragment={selectedScanExport}
                        loading={selectedScanExportLoading}
                    />
                </ScansContainer>
            )}
        </PatientDetailCard>
    );
};
